const SCREENSHOT_FILENAME = 'screenshot.png';
const SCREENSHOT_MIME = 'image/png';

/**
 * Returns true for elements that should be absent from the screenshot:
 * - The bug-report trigger button itself
 * - MUI modal/dialog/popover portals rendered under <body>
 * - The react-hot-toast notification container
 *
 * modern-screenshot walks the DOM and calls this on every node; returning
 * true excludes the subtree rooted at that node.
 */
const shouldIgnoreElement = (element: Element): boolean => {
  if (
    element instanceof HTMLElement &&
    element.dataset.testid === 'bug-report-trigger'
  ) {
    return true;
  }

  return (
    element.classList.contains('MuiDialog-root') ||
    element.classList.contains('MuiModal-root') ||
    element.classList.contains('MuiPopover-root') ||
    element.classList.contains('MuiMenu-root') ||
    element.id === '_rht_toaster'
  );
};

/**
 * Captures a full-page screenshot using modern-screenshot.
 *
 * Unlike html2canvas — which re-implements the CSS painting spec and therefore
 * fails on modern color functions like oklch — modern-screenshot serialises the
 * DOM into an SVG <foreignObject> and delegates all rendering to the browser's
 * own compositor. This means every CSS feature (oklch, color-mix, container
 * queries, etc.) works without any special handling.
 *
 * The library is loaded lazily so it only enters the bundle when the user
 * actually clicks the "Take screenshot" button.
 *
 * Known limitation: cross-origin <img> tags without CORS headers are rendered
 * as blank (same as html2canvas). Sentry profile images and third-party CDN
 * assets may appear missing.
 */
export const captureScreenshot = async (): Promise<File> => {
  const { domToBlob } = await import('modern-screenshot');

  const { body, documentElement } = globalThis.document;

  const width = Math.max(
    body.scrollWidth,
    body.offsetWidth,
    documentElement.scrollWidth,
    documentElement.offsetWidth,
  );

  const height = Math.max(
    body.scrollHeight,
    body.offsetHeight,
    documentElement.scrollHeight,
    documentElement.offsetHeight,
  );

  const blob = await domToBlob(body, {
    filter: (node) =>
      node instanceof Element ? !shouldIgnoreElement(node) : true,
    height,
    // Cap at 2× to avoid exceeding browser canvas size limits on high-DPR
    // displays.
    scale: Math.min(globalThis.devicePixelRatio || 1, 2),
    type: SCREENSHOT_MIME,
    width,
  });

  if (!blob) {
    throw new Error('domToBlob returned null.');
  }

  return new File([blob], SCREENSHOT_FILENAME, { type: SCREENSHOT_MIME });
};
